import Style from './container.module.scss';
import { useEditor, useNode } from '@craftjs/core';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getCloneTree } from 'src/components/formEditor/uiEditor/uiEditor-helper';
import { useAnswerRowConditional } from 'src/features/generalForms/answerPreview/hooks/useAnswerRowConditional';
import { generateNestedDataKey } from 'src/features/generalForms/builder/layout/panels/helpers/helper';
import {
  FormRepeatableContainerComponent,
  FormRepeatableSection,
} from 'src/features/generalForms/builder/types/RepeatableContainer';
import { AppButton } from 'uikit/buttons';
import { Collapse } from 'uikit/collapse/Collapse';
import { ReactComponent as Arrow } from 'src/assets/img/generalBuilder/b_arrow_24.svg';

export const RepeatableContainer: FormRepeatableContainerComponent = ({ dataKey: rootDataKey, children, title }) => {
  const { actions, query } = useEditor();
  const { visible } = useAnswerRowConditional();

  const { node: parentNode } = useNode((n) => ({
    styles: n.data.custom,
    node: n,
  }));

  const form = useFormContext();
  const fieldOrder = useWatch({ control: form.control, name: `${rootDataKey!}.order` }) ?? [];
  const addSection = useCallback(
    (sectionDataKey: string) => {
      const newTree = getCloneTree(query, parentNode.data.linkedNodes.id, generateNestedDataKey(query, sectionDataKey));
      actions.addNodeTree(newTree, parentNode.id);
    },
    [actions, parentNode.data.linkedNodes.id, parentNode.id, query],
  );

  // TODO: Refactor this code if it's a slow
  useEffect(() => {
    // IMPORTANT: There will be a slight delay to allow Craft.js to load ROOT.
    const timer = setTimeout(() => {
      parentNode.data.nodes.forEach((nodeId) => actions.delete(nodeId));
      fieldOrder.forEach(addSection);
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldOrder]);

  if (!visible) {
    return <></>;
  }

  return (
    <div className={Style.repeatableContainer}>
      {title && (
        <div className={Style.title}>
          <p>{title}</p>
        </div>
      )}
      {children}
    </div>
  );
};

export const RepeatableSection: FormRepeatableSection = ({ children, sectionTitle, isCollapsible }) => {
  const { query } = useEditor();
  const { id, parentNodeId } = useNode((n) => ({
    parentNodeId: n.data.parent,
    styles: n.data.custom,
  }));

  const [expanded, setExpanded] = useState(!isCollapsible);

  const sectionIndex = useMemo(
    () => query.node(parentNodeId).descendants(false, 'childNodes').indexOf(id) + 1,
    [id, parentNodeId, query],
  );

  if (!sectionTitle) return <div className={Style.section}>{children}</div>;

  return (
    <div className={Style.section}>
      <div className={Style.header}>
        {sectionTitle && <div>{`${sectionTitle} ${sectionIndex}`}</div>}
        {isCollapsible && (
          <AppButton
            className={clsx(Style.collapseButton, { [Style.collapsed]: !expanded })}
            variant={'icon-link'}
            colorSchema={'primary'}
            Icon={Arrow}
            onClick={() => setExpanded((x) => !x)}
          />
        )}
      </div>
      <Collapse in={expanded} unmountOnExit>
        <div className={Style.content}>{children}</div>
      </Collapse>
    </div>
  );
};
